import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { usePracticeStore } from '@/store/practiceStore';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';

import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiClient } from '@/utils/api';
import { useAuthStore } from '@/store/authStore';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { findMatchingPracticeFoldersElectron } from '@/utils/practiceFolderScan';
import { normalizeDocumentPath, DEFAULT_NAS_PICKER_ROOT } from '@/utils/normalizeDocumentPath';
import { sanitizeFolderName } from '@/utils/sanitizeFolderName';
import { useClientDocumentsConfig } from '@/hooks/useClientDocumentsConfig';

type Step = 'type' | 'info' | 'dates' | 'review';

type NasChoiceResult =
  | { action: 'associate'; folderName: string; createSubfolders: boolean }
  | { action: 'create' };

export default function PracticeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [formData, setFormData] = useState({
    typeId: '',
    practiceNumber: '',
    title: '',
    description: '',
    clientId: '',
    interventionLocation: '',
    priority: 'MEDIUM',
    startDate: '',
    dueDate: '',
    amount: '',
  });

  const {
    currentPractice: practice,
    fetchPractice,
    updatePractice,
    loading,
  } = usePracticeStore();

  const {
    practiceTypes,
    fetchPracticeTypes,
    loading: typesLoading,
  } = usePracticeTypeStore();

  const [clients, setClients] = useState<Array<{ id: string; firstName: string; lastName: string; email?: string; companyName?: string | null }>>([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  const { documentsNasRoot } = useClientDocumentsConfig();
  const inElectron = isElectronEnvironment() || !!(typeof window !== 'undefined' && window.isElectron);

  // NAS dialog state
  const [showNasDialog, setShowNasDialog] = useState(false);
  const [nasCandidates, setNasCandidates] = useState<string[]>([]);
  const [selectedNasFolder, setSelectedNasFolder] = useState('');
  const [nasCreateSubfolders, setNasCreateSubfolders] = useState(true);
  const nasChoiceResolverRef = useRef<((r: NasChoiceResult) => void) | null>(null);
  const [pendingPracticeId, setPendingPracticeId] = useState<string | null>(null);

  const waitForNasFolderChoice = (candidates: string[]): Promise<NasChoiceResult> => {
    return new Promise((resolve) => {
      nasChoiceResolverRef.current = resolve;
      setNasCandidates(candidates);
      setSelectedNasFolder(candidates[0] || '');
      setNasCreateSubfolders(true);
      setShowNasDialog(true);
    });
  };

  const resolveNasChoice = (result: NasChoiceResult) => {
    setShowNasDialog(false);
    const fn = nasChoiceResolverRef.current;
    nasChoiceResolverRef.current = null;
    fn?.(result);
  };

  // Carica pratica esistente
  useEffect(() => {
    if (id) {
      fetchPractice(id).catch(() => {
        toast.error('Errore nel caricamento della pratica');
        navigate('/practices');
      });
    }
  }, [id, fetchPractice, navigate]);

  useEffect(() => {
    fetchPracticeTypes(false);
    apiClient.get('/clients/combobox')
      .then((res) => setClients(res.data?.data || []))
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  }, [fetchPracticeTypes]);

  // Pre-compila form quando pratica è caricata
  useEffect(() => {
    if (practice) {
      setFormData({
        typeId: practice.typeId || practice.type?.id || '',
        practiceNumber: practice.practiceNumber || '',
        title: practice.title || '',
        description: practice.description || '',
        clientId: practice.clientId || practice.client?.id || '',
        interventionLocation: practice.interventionLocation || '',
        priority: practice.priority || 'MEDIUM',
        startDate: practice.startDate ? format(new Date(practice.startDate), 'yyyy-MM-dd') : '',
        dueDate: practice.dueDate ? format(new Date(practice.dueDate), 'yyyy-MM-dd') : '',
        amount: practice.amount ? String(practice.amount) : '',
      });
    }
  }, [practice]);

  // Track the original practice values to detect auto-generated titles
  const [originalTitle, setOriginalTitle] = useState('');
  useEffect(() => {
    if (practice) setOriginalTitle(practice.title || '');
  }, [practice]);

  // Auto-regenerate title when type or client change (only if title matches auto pattern)
  useEffect(() => {
    if (!formData.typeId || !formData.clientId || !practice) return;
    const type = practiceTypes.find(t => t.id === formData.typeId);
    const client = clients.find(c => c.id === formData.clientId);
    if (!type || !client) return;

    const newAutoTitle = `${type.code || type.name} - ${client.firstName} ${client.lastName}`;
    const oldType = practiceTypes.find(t => t.id === (practice.typeId || practice.type?.id));
    const oldClient = clients.find(c => c.id === (practice.clientId || practice.client?.id));
    if (oldType && oldClient) {
      const oldAutoTitle = `${oldType.code || oldType.name} - ${oldClient.firstName} ${oldClient.lastName}`;
      if (formData.title === oldAutoTitle || formData.title === originalTitle) {
        if (newAutoTitle !== formData.title) {
          setFormData(prev => ({ ...prev, title: newAutoTitle }));
        }
      }
    }
  }, [formData.typeId, formData.clientId, practiceTypes, clients]);

  const steps: { id: Step; title: string; description: string }[] = [
    { id: 'type', title: 'Tipo Pratica', description: 'Seleziona il tipo di pratica' },
    { id: 'info', title: 'Informazioni', description: 'Titolo, cliente e descrizione' },
    { id: 'dates', title: 'Scadenze', description: 'Date e priorità' },
    { id: 'review', title: 'Riepilogo', description: 'Verifica i dati inseriti' },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const canGoNext = () => {
    switch (currentStep) {
      case 'type':
        return !!formData.typeId;
      case 'info':
        return !!formData.title && !!formData.clientId;
      case 'dates':
        return true; // Tutti i campi sono opzionali
      case 'review':
        return false;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canGoNext()) {
      if (currentStepIndex < steps.length - 1) {
        setCurrentStep(steps[currentStepIndex + 1].id);
      }
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const handleNasAfterUpdate = async (
    practiceId: string,
    newPracticeNumber: string
  ) => {
    if (!inElectron || !window.electronFS) return;
    const efs = window.electronFS;
    const volumePath = normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;

    if (!efs.directoryExists(volumePath)) {
      toast.error('Percorso NAS non raggiungibile');
      return;
    }

    // Search for folders matching the NEW practice number
    if (newPracticeNumber && newPracticeNumber.length >= 2) {
      const candidates = findMatchingPracticeFoldersElectron(volumePath, newPracticeNumber);
      if (candidates.length > 0) {
        setPendingPracticeId(practiceId);
        const choice = await waitForNasFolderChoice(candidates);
        if (choice.action === 'associate') {
          const fullPath = efs.joinPath(volumePath, choice.folderName);
          const uncPath = `${documentsNasRoot || ''}\\${choice.folderName}`;
          await apiClient.patch(`/practices/${practiceId}/document-path`, { documentPath: uncPath });

          if (choice.createSubfolders) {
            const type = practiceTypes.find(t => t.id === formData.typeId);
            const subfolders = (type as any)?.folderSubfolders || [];
            for (const sub of subfolders) {
              if (sub) {
                try { efs.createDirectory(efs.joinPath(fullPath, sanitizeFolderName(sub))); } catch {}
              }
            }
          }
          toast.success('Cartella NAS associata');
        } else {
          await createNewFolderElectron(practiceId, volumePath);
        }
        return;
      }
    }

    // No candidates — create new folder
    await createNewFolderElectron(practiceId, volumePath);
  };

  const createNewFolderElectron = async (practiceId: string, volumePath: string) => {
    const efs = window.electronFS;
    if (!efs) return;

    const type = practiceTypes.find(t => t.id === formData.typeId);
    const client = clients.find(c => c.id === formData.clientId);
    const FOLDER_TEMPLATE_DEFAULT = '{practiceNumber} - {clientName} - {interventionLocation} - {practiceTypeName}';
    const folderTemplate = (type as any)?.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT;

    const rawName = folderTemplate
      .replace('{practiceNumber}', formData.practiceNumber || 'SENZA_NUMERO')
      .replace('{clientName}', client ? `${client.firstName} ${client.lastName}` : '')
      .replace('{interventionLocation}', formData.interventionLocation || 'N/D')
      .replace('{practiceTypeName}', type?.name || '');
    const folderName = sanitizeFolderName(rawName);
    const fullPath = efs.joinPath(volumePath, folderName);

    try {
      efs.createDirectory(fullPath);
      const subfolders = (type as any)?.folderSubfolders || [];
      for (const sub of subfolders) {
        if (sub) efs.createDirectory(efs.joinPath(fullPath, sanitizeFolderName(sub)));
      }
      const uncPath = `${documentsNasRoot || ''}\\${folderName}`;
      await apiClient.patch(`/practices/${practiceId}/document-path`, { documentPath: uncPath });
      toast.success('Nuova cartella creata sul NAS');
    } catch (err: any) {
      console.error('Error creating folder:', err);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;

    try {
      const practiceData: any = {
        typeId: formData.typeId,
        practiceNumber: formData.practiceNumber || undefined,
        title: formData.title,
        description: formData.description || undefined,
        clientId: formData.clientId,
        interventionLocation: formData.interventionLocation || undefined,
        priority: formData.priority || 'MEDIUM',
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
        dueDate: formData.dueDate || undefined,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
      };

      const response = await apiClient.put(`/practices/${id}`, practiceData);
      const cascade = response.data?.data?.cascadeUpdates;

      let msg = 'Pratica modificata con successo';
      if (cascade?.activitiesUpdated > 0) {
        msg += ` (${cascade.activitiesUpdated} attività aggiornate)`;
      }
      if (cascade?.titleAutoRegenerated) {
        msg += ' — titolo rigenerato automaticamente';
      }
      toast.success(msg);

      // Handle NAS folder reassociation if documentPath changed
      if (cascade?.oldDocumentPath && cascade?.newDocumentPath && cascade.oldDocumentPath !== cascade.newDocumentPath) {
        if (inElectron) {
          await handleNasAfterUpdate(id, formData.practiceNumber);
        }
      }

      navigate(`/practices/${id}`);
    } catch (error: any) {
      console.error('Error updating practice:', error);
      toast.error(error.response?.data?.message || 'Errore nella modifica della pratica');
    }
  };

  if (!practice) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/practices')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alle pratiche
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedType = practiceTypes.find(t => t.id === formData.typeId);
  const selectedClient = clients.find(c => c.id === formData.clientId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/practices/${id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alla pratica
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Modifica Pratica</h1>
            <p className="mt-1 text-sm text-gray-500">
              Modifica i dati della pratica: {practice.title}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = currentStepIndex > index;
              const stepNumber = index + 1;

              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center font-semibold
                        ${isActive ? 'bg-blue-600 text-white' : ''}
                        ${isCompleted ? 'bg-green-600 text-white' : ''}
                        ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-600' : ''}
                      `}
                    >
                      {isCompleted ? '✓' : stepNumber}
                    </div>
                    <div className="mt-2 text-center">
                      <div className={`text-sm font-medium ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{step.description}</div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-1 flex-1 mx-4 ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Form Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStepIndex].title}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Step 1: Tipo Pratica */}
          {currentStep === 'type' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="typeId">Tipo Pratica *</Label>
                {typesLoading ? (
                  <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    Caricamento...
                  </div>
                ) : (
                  <Combobox
                    options={practiceTypes
                      .filter(type => type.isActive)
                      .sort((a, b) => a.order - b.order)
                      .map((type) => ({
                        value: type.id,
                        label: `${type.icon || ''} ${type.name}`.trim(),
                        description: type.description || undefined,
                      }))}
                    value={formData.typeId}
                    onValueChange={(value) => setFormData({ ...formData, typeId: value })}
                    placeholder="Seleziona tipo pratica"
                    searchPlaceholder="Cerca tipo pratica..."
                    emptyMessage="Nessun tipo pratica trovato"
                  />
                )}
                {selectedType && selectedType.description && (
                  <p className="mt-2 text-sm text-gray-600">{selectedType.description}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Informazioni */}
          {currentStep === 'info' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="practiceNumber">Numero Pratica (opzionale)</Label>
                <Input
                  id="practiceNumber"
                  value={formData.practiceNumber}
                  onChange={(e) => setFormData({ ...formData, practiceNumber: e.target.value.toUpperCase() })}
                  placeholder="Es: A12345 (max 6 caratteri alfanumerici)"
                  maxLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Numero per tracciare i faldoni fisici
                </p>
              </div>

              <div>
                <Label htmlFor="title">Titolo Pratica *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Es: SCIA per ristrutturazione Via Roma 123"
                />
              </div>

              <div>
                <Label htmlFor="clientId">Cliente *</Label>
                {clientsLoading ? (
                  <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center">
                    Caricamento...
                  </div>
                ) : (
                  <Combobox
                    options={clients.map(client => ({
                      value: client.id,
                      label: client.companyName
                        || `${client.lastName || ''} ${client.firstName || ''}`.trim()
                        || `${client.firstName} ${client.lastName}`.trim(),
                      description: client.email || undefined,
                    }))}
                    value={formData.clientId}
                    onValueChange={(value) => setFormData({ ...formData, clientId: value })}
                    placeholder="Seleziona cliente"
                    searchPlaceholder="Cerca cliente..."
                    emptyMessage="Nessun cliente trovato"
                  />
                )}
              </div>

              <div>
                <Label htmlFor="interventionLocation">Luogo Intervento</Label>
                <Input
                  id="interventionLocation"
                  value={formData.interventionLocation}
                  onChange={(e) => setFormData({ ...formData, interventionLocation: e.target.value })}
                  placeholder="Es: Via Roma 123, Milano"
                />
              </div>

              <div>
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione dettagliata della pratica..."
                  rows={5}
                />
              </div>
            </div>
          )}

          {/* Step 3: Scadenze */}
          {currentStep === 'dates' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="startDate">Data Inizio</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Per pratiche vecchie, inserire la data di inizio originale
                </p>
              </div>

              <div>
                <Label htmlFor="priority">Priorità</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Bassa</SelectItem>
                    <SelectItem value="MEDIUM">Media</SelectItem>
                    <SelectItem value="HIGH">Alta</SelectItem>
                    <SelectItem value="URGENT">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dueDate">Data Scadenza</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>

              {user?.canAccessQuotesAndAccounts && (
              <div>
                <Label htmlFor="amount">Importo storico (€)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  readOnly
                  disabled
                  placeholder="0.00"
                />
              </div>
              )}
            </div>
          )}

          {/* Step 4: Riepilogo */}
          {currentStep === 'review' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3">Riepilogo Modifiche</h3>
                <div className="space-y-2 text-sm">
                  {formData.practiceNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">N° Pratica:</span>
                      <span className="font-medium font-mono">{formData.practiceNumber}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tipo:</span>
                    <span className="font-medium">{selectedType?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Titolo:</span>
                    <span className="font-medium">{formData.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cliente:</span>
                    <span className="font-medium">
                      {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'N/A'}
                    </span>
                  </div>
                  {formData.interventionLocation && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Luogo Intervento:</span>
                      <span className="font-medium">{formData.interventionLocation}</span>
                    </div>
                  )}
                  {formData.description && (
                    <div>
                      <span className="text-gray-600">Descrizione:</span>
                      <p className="mt-1 text-gray-800">{formData.description}</p>
                    </div>
                  )}
                  {formData.startDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Data Inizio:</span>
                      <span className="font-medium">{new Date(formData.startDate).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Priorità:</span>
                    <span className="font-medium">{formData.priority}</span>
                  </div>
                  {formData.dueDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scadenza:</span>
                      <span className="font-medium">{new Date(formData.dueDate).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  {user?.canAccessQuotesAndAccounts && formData.amount && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Importo:</span>
                      <span className="font-medium">€{parseFloat(formData.amount).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStepIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Indietro
            </Button>
            <div className="flex gap-2">
              {currentStep === 'review' ? (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Salvataggio...' : 'Salva Modifiche'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!canGoNext()}
                >
                  Avanti
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={showNasDialog}
        onOpenChange={(open) => {
          if (!open && nasChoiceResolverRef.current) {
            resolveNasChoice({ action: 'create' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cartelle trovate sul NAS</DialogTitle>
            <DialogDescription>
              Esistono cartelle il cui nome inizia con il numero pratica <span className="font-mono font-semibold">{formData.practiceNumber}</span>.
              Puoi associare una cartella esistente oppure crearne una nuova.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Cartella da associare</Label>
              <Select value={selectedNasFolder} onValueChange={setSelectedNasFolder}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona cartella" />
                </SelectTrigger>
                <SelectContent>
                  {nasCandidates.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="nasSubfolders"
                checked={nasCreateSubfolders}
                onCheckedChange={(v) => setNasCreateSubfolders(v === true)}
              />
              <Label htmlFor="nasSubfolders" className="text-sm">
                Crea sotto-cartelle mancanti dal template
              </Label>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => resolveNasChoice({ action: 'create' })}>
              Crea nuova cartella
            </Button>
            <Button
              onClick={() => resolveNasChoice({
                action: 'associate',
                folderName: selectedNasFolder,
                createSubfolders: nasCreateSubfolders,
              })}
              disabled={!selectedNasFolder}
            >
              Associa cartella selezionata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
